/**
 * ScrollAwareHeader Component
 *
 * A smart header component that responds to scroll events with:
 * - Auto-hide/show based on scroll direction
 * - Background opacity and blur effects
 * - Compact mode when scrolled down
 * - Smooth transitions and animations
 * - Mobile-responsive behavior
 * - Accessibility features
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  BellIcon,
  UserCircleIcon,
  Bars3Icon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { ComponentErrorBoundary } from "../ErrorBoundary";

/**
 * Custom hook for scroll detection and behavior
 */
function useScrollAware({
  threshold = 10,
  hideOnScrollDown = true,
  compactThreshold = 50,
  blurThreshold = 20,
} = {}) {
  const [scrollState, setScrollState] = useState({
    scrollY: 0,
    scrollDirection: "up",
    isScrolled: false,
    isHidden: false,
    isCompact: false,
    shouldBlur: false,
  });

  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  const updateScrollState = useCallback(() => {
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    const scrollDirection = scrollY > lastScrollY.current ? "down" : "up";
    const scrollDelta = Math.abs(scrollY - lastScrollY.current);

    // Only update if scroll delta is significant enough
    if (scrollDelta < threshold) {
      ticking.current = false;
      return;
    }

    const newState = {
      scrollY,
      scrollDirection,
      isScrolled: scrollY > threshold,
      isHidden:
        hideOnScrollDown && scrollDirection === "down" && scrollY > threshold,
      isCompact: scrollY > compactThreshold,
      shouldBlur: scrollY > blurThreshold,
    };

    setScrollState(newState);
    lastScrollY.current = scrollY;
    ticking.current = false;
  }, [threshold, hideOnScrollDown, compactThreshold, blurThreshold]);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(updateScrollState);
      ticking.current = true;
    }
  }, [updateScrollState]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return scrollState;
}

/**
 * Main ScrollAwareHeader Component
 */
export default function ScrollAwareHeader({
  children,
  variant = "default", // 'default' | 'compact' | 'floating' | 'minimal'
  behavior = "auto-hide", // 'auto-hide' | 'compact' | 'blur' | 'fixed'
  className = "",
  searchPlaceholder = "Search...",
  onSidebarToggle,
  onSearch,
  onNotificationClick,
  onProfileClick,
  user,
  logo,
  actions,
  showSearch = true,
  showNotifications = true,
  showProfile = true,
  stickyOffset = 0,
  ...props
}) {
  const scrollState = useScrollAware({
    hideOnScrollDown: behavior === "auto-hide",
    compactThreshold: 50,
    blurThreshold: 20,
  });

  const [searchValue, setSearchValue] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Header visibility and styling based on scroll state and behavior
  const getHeaderClasses = () => {
    const baseClasses = `
      fixed top-0 left-0 right-0 z-60 transition-all duration-300 ease-in-out
      ${className}
    `;

    const variantClasses = {
      default: "bg-white border-b border-gray-200",
      compact: "bg-white/95 backdrop-blur-md border-b border-gray-200/50",
      floating:
        "bg-white/90 backdrop-blur-lg border border-gray-200/30 rounded-lg mx-4 mt-4",
      minimal: "bg-transparent",
    };

    const behaviorClasses = {
      "auto-hide": scrollState.isHidden ? "-translate-y-full" : "translate-y-0",
      compact: scrollState.isCompact ? "py-2" : "py-4",
      blur: scrollState.shouldBlur
        ? "bg-white/95 backdrop-blur-md"
        : "bg-white",
      fixed: "translate-y-0",
    };

    // Apply blur effect when scrolled
    const blurEffect =
      scrollState.shouldBlur && behavior !== "minimal"
        ? "bg-white/95 backdrop-blur-md shadow-sm"
        : "";

    // Compact mode height adjustment - works with any behavior when scrolled
    const heightClass = scrollState.isCompact ? "h-14" : "h-16";

    return `${baseClasses} ${variantClasses[variant]} ${behaviorClasses[behavior]} ${blurEffect} ${heightClass}`;
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (onSearch && searchValue.trim()) {
      onSearch(searchValue.trim());
    }
  };

  return (
    <ComponentErrorBoundary componentName="ScrollAwareHeader">
      <header
        className={getHeaderClasses()}
        style={{ top: stickyOffset }}
        {...props}
      >
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          {/* Left section */}
          <div className="flex items-center space-x-4 flex-1">
            {/* Sidebar toggle (mobile) */}
            {onSidebarToggle && (
              <button
                className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
                onClick={onSidebarToggle}
                aria-label="Toggle sidebar"
              >
                <Bars3Icon className="h-6 w-6 text-gray-600" />
              </button>
            )}

            {/* Logo */}
            {logo && (
              <div
                className={`flex-shrink-0 transition-all duration-300 ${
                  scrollState.isCompact ? "scale-90" : "scale-100"
                }`}
              >
                {typeof logo === "function" ? logo(scrollState) : logo}
              </div>
            )}

            {/* Search */}
            {showSearch && (
              <div
                className={`hidden md:block flex-1 max-w-lg transition-all duration-300 ${
                  scrollState.isCompact ? "max-w-md" : "max-w-lg"
                }`}
              >
                <form onSubmit={handleSearchSubmit} className="relative">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      placeholder={searchPlaceholder}
                      className={`
                        w-full pl-10 pr-4 rounded-lg border border-gray-300 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                        transition-all duration-200
                        ${scrollState.isCompact ? "py-2 text-sm" : "py-2.5"}
                        ${isSearchFocused ? "shadow-md" : "shadow-sm"}
                      `}
                    />
                  </div>

                  {/* Search suggestions could go here */}
                  {isSearchFocused && searchValue && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-70">
                      <div className="p-3 text-sm text-gray-500">
                        Press Enter to search for "{searchValue}"
                      </div>
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>

          {/* Right section */}
          <div className="flex items-center space-x-3">
            {/* Custom actions */}
            {actions && (
              <div className="hidden lg:flex items-center space-x-2">
                {actions.map((action, index) => (
                  <div key={index}>{action}</div>
                ))}
              </div>
            )}

            {/* Notifications */}
            {showNotifications && (
              <button
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors relative"
                onClick={onNotificationClick}
                aria-label="Notifications"
              >
                <BellIcon className="h-5 w-5" />
                {/* Notification badge */}
                <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
              </button>
            )}

            {/* User profile */}
            {showProfile && (
              <UserProfile
                user={user}
                onProfileClick={onProfileClick}
                isCompact={scrollState.isCompact}
              />
            )}
          </div>
        </div>

        {/* Mobile search bar */}
        {showSearch && (
          <div className="md:hidden border-t border-gray-200 px-4 py-3">
            <form onSubmit={handleSearchSubmit}>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </form>
          </div>
        )}

        {/* Children content */}
        {children}
      </header>
    </ComponentErrorBoundary>
  );
}

/**
 * User Profile Component
 */
function UserProfile({ user, onProfileClick, isCompact }) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (!user) {
    return (
      <button
        className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
        onClick={onProfileClick}
        aria-label="User profile"
      >
        <UserCircleIcon className="h-6 w-6" />
      </button>
    );
  }

  return (
    <div className="relative">
      <button
        className={`flex items-center space-x-2 p-2 rounded-md hover:bg-gray-100 transition-all duration-200 ${
          isCompact ? "space-x-1" : "space-x-2"
        }`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        aria-label="User menu"
      >
        {/* Avatar */}
        {user.avatar ? (
          <img
            className={`rounded-full transition-all duration-200 ${
              isCompact ? "h-7 w-7" : "h-8 w-8"
            }`}
            src={user.avatar}
            alt={user.name}
          />
        ) : (
          <div
            className={`bg-blue-600 rounded-full flex items-center justify-center transition-all duration-200 ${
              isCompact ? "h-7 w-7" : "h-8 w-8"
            }`}
          >
            <span
              className={`text-white font-medium ${
                isCompact ? "text-xs" : "text-sm"
              }`}
            >
              {user.name?.charAt(0) || "U"}
            </span>
          </div>
        )}

        {/* Name (hidden on mobile and when compact) */}
        <span
          className={`hidden lg:block text-sm font-medium text-gray-700 transition-all duration-200 ${
            isCompact ? "text-xs" : "text-sm"
          }`}
        >
          {user.name}
        </span>

        <ChevronDownIcon
          className={`text-gray-400 transition-transform duration-200 ${
            isDropdownOpen ? "rotate-180" : ""
          } ${isCompact ? "h-3 w-3" : "h-4 w-4"}`}
        />
      </button>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="py-1">
            <div className="px-4 py-2 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => {
                onProfileClick?.();
                setIsDropdownOpen(false);
              }}
            >
              Profile Settings
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              onClick={() => setIsDropdownOpen(false)}
            >
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Logo Component with responsive sizing
 */
export function ResponsiveLogo({
  title = "RE Platform",
  icon = "🏢",
  isCompact,
}) {
  return (
    <div className="flex items-center space-x-3">
      <span
        className={`transition-all duration-300 ${isCompact ? "text-xl" : "text-2xl"}`}
      >
        {icon}
      </span>
      <span
        className={`font-bold text-gray-900 transition-all duration-300 ${
          isCompact ? "text-lg" : "text-xl"
        }`}
      >
        {title}
      </span>
    </div>
  );
}

/**
 * Header with breadcrumbs
 */
export function BreadcrumbHeader({
  breadcrumbs = [],
  currentPage,
  scrollBehavior = "blur",
  ...props
}) {
  return (
    <ScrollAwareHeader behavior={scrollBehavior} {...props}>
      <div className="border-t border-gray-200 px-4 py-2">
        <nav className="flex items-center space-x-2 text-sm">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="text-gray-400">/</span>}
              <a
                href={crumb.href}
                className="text-blue-600 hover:text-blue-700 transition-colors"
              >
                {crumb.label}
              </a>
            </React.Fragment>
          ))}
          {currentPage && (
            <>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700 font-medium">{currentPage}</span>
            </>
          )}
        </nav>
      </div>
    </ScrollAwareHeader>
  );
}

/**
 * Minimal floating header
 */
export function FloatingHeader({ children, ...props }) {
  return (
    <ScrollAwareHeader
      variant="floating"
      behavior="auto-hide"
      className="max-w-4xl mx-auto"
      {...props}
    >
      {children}
    </ScrollAwareHeader>
  );
}
